// src/pages/user/messages/UserMessages.jsx

import React, { useState, useRef, useEffect, useContext } from "react";
import {
  FiSearch,
  FiSend,
  FiChevronLeft,
  FiTruck,
  FiPaperclip,
} from "react-icons/fi";
import { BsBellFill } from "react-icons/bs";
import UserMasterLayout from "../../../layout/useradmin/UserMasterLayout";
import "../../../assets/css/useradmin/messages/Messages.css";
import axios from "axios";
import Swal from "sweetalert2";
import AppContext from "../../../context/AppContext";
import { useLocation } from "react-router-dom";
import { getEcho } from "../../../utils/echo";
// ─── Status badge color helper ─────────────────────────────────────────────────
const statusClass = (s) =>
  s === "Approved"
    ? "um-status--approved"
    : s === "Rejected"
      ? "um-status--rejected"
      : "um-status--pending";

// ─── Avatar color pool ─────────────────────────────────────────────────────────
const avatarColors = ["#FDB714", "#000000", "#2ecc71", "#e67e22"];

// ══════════════════════════════════════════════════════════════════════════════
const UserMessages = () => {
  const { baseUrl, baseUrlForFile } = useContext(AppContext);
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [newMsg, setNewMsg] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [attachedFile, setAttachedFile] = useState(null);
  const [showSidebar, setShowSidebar] = useState(true);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ── PUSHER — real-time naye messages sunta hai jo select ki hui
  //    conversation mein aayen (jab tak yeh chat khuli hai) ──
  useEffect(() => {
    if (!selectedChat) return;

    const echo = getEcho(baseUrl);
    if (!echo) return;

    const channel = echo.private(`conversation.${selectedChat.id}`);

    channel.listen(".new-message", (e) => {
      setMessages((prev) => [...prev, e.chat]);
    });

    return () => {
      echo.leave(`conversation.${selectedChat.id}`);
    };
  }, [selectedChat, baseUrl]);

  // ── fetchAllConversations ──────────────────────────────────────────────────
  const fetchAllConversations = () => {
    const savedCustomer = JSON.parse(localStorage.getItem("LoggedInCustomer"));
    const token = localStorage.getItem("authToken");

    const formData = new FormData();
    formData.append("srcustomer_id", savedCustomer.id);

    axios
      .post(
        `${baseUrl}/customer/conversations/fetchAllConversations`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => {
        if (res.data.success === true) {
          setConversations(res.data.conversations);
        } else {
          Swal.fire({ icon: "error", title: "Error", text: res.data.message });
        }
      })
      .catch((err) => {
        console.log("Error fetching conversations:", err);
      });
  };

  useEffect(() => {
    fetchAllConversations();
  }, []);

  useEffect(() => {
    if (conversations.length > 0 && location.state?.openConversationId) {
      const targetConversation = conversations.find(
        (c) => c.id === location.state.openConversationId,
      );

      if (targetConversation) {
        setSelectedChat(targetConversation);
        fetchAllMessages(targetConversation.id);
      }
    }
  }, [conversations, location.state]);

  // ── fetchAllMessages ───────────────────────────────────────────────────────
  const fetchAllMessages = (conversationId) => {

    const token = localStorage.getItem("authToken");

    const formData = new FormData();
    formData.append("srconversation_id", conversationId);

    axios
      .post(`${baseUrl}/customer/chat/fetchAllMessages`, formData, {
         headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        console.log("Messages response:", res.data); // debug ke liye
        if (res.data.success === true) {
          setMessages(res.data.messages);
        } else {
          // Swal hata do yahan sy — sirf empty set karo
          setMessages([]);
        }
      })
      .catch((err) => {
        console.log("Error fetching messages:", err);
      });
  };

  // ── sendMessage ────────────────────────────────────────────────────────────
  const sendMessage = () => {
    if (!newMsg.trim() && !attachedFile) return;
    if (!selectedChat) return;

    const token = localStorage.getItem("authToken");

    const formData = new FormData();
    formData.append("srconversation_id", selectedChat.id);
    formData.append("sender", "user");
    formData.append("message", newMsg);
    if (attachedFile) {
      formData.append("image", attachedFile);
    }

    axios
      .post(`${baseUrl}/customer/chat/sendMessage`, formData, {
         headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        if (res.data.success === true) {
          setNewMsg("");
          setAttachedFile(null);
          if (fileInputRef.current) fileInputRef.current.value = "";
          fetchAllMessages(selectedChat.id);
        } else {
          Swal.fire({ icon: "error", title: "Error", text: res.data.message });
        }
      })
      .catch((err) => {
        console.log("Error sending message:", err);
      });
  };

  // ── handleSelectConv ───────────────────────────────────────────────────────
  const handleSelectConv = (conv) => {
    setSelectedChat(conv);
    fetchAllMessages(conv.id);
  };

  // ── Filter conversations ───────────────────────────────────────────────────
  const filtered = conversations?.filter((c) => {
    const rentalName = c.srrentaladmin?.fullname?.toLowerCase() || "";
    const mechanicName = c.srmechanicadmin?.fullname?.toLowerCase() || "";
    const term = searchTerm.toLowerCase();
    return rentalName.includes(term) || mechanicName.includes(term);
  });

  // ── formatTime ────────────────────────────────────────────────────────────
  const formatTime = (datetime) => {
    if (!datetime) return "";
    const date = new Date(datetime);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <UserMasterLayout>
      <div className="um-page">
        <div className="um-body">
          {/* ── SIDEBAR ── */}
          <aside
            className={`um-sidebar ${showSidebar ? "um-sidebar--open" : "um-sidebar--hidden"}`}
          >
            <div className="um-sidebar-header">
              <div className="um-sidebar-title-row">
                <div className="um-sidebar-icon-wrap">
                  <FiSend size={16} />
                </div>
                <div>
                  <div className="um-sidebar-title">Messages</div>
                  <div className="um-sidebar-sub">
                    {conversations?.length} conversations
                  </div>
                </div>
              </div>

              <div className="um-sidebar-search-wrap">
                <FiSearch className="um-sidebar-search-icon" />
                <input
                  className="um-sidebar-search"
                  placeholder="Search farmer or equipment..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="um-conv-list">
              {filtered?.map((c, idx) => (
                <div
                  key={c.id}
                  className={`um-conv-item ${selectedChat?.id === c.id ? "um-conv-item--active" : ""}`}
                  onClick={() => handleSelectConv(c)}
                >
                  <div className="um-conv-avatar">
                    <img
                      src={`${baseUrlForFile}/${
                        c.srrentaladmin
                          ? c.srrentaladmin?.image
                          : c.srmechanicadmin?.image
                      }`}
                      alt="profile"
                      className="um-avatar-img"
                      onError={(e) => {
                        e.target.src = "/images/default-user.png";
                      }}
                    />
                  </div>

                  <div className="um-conv-info">
                    <div className="um-conv-row1">
                      <span className="um-conv-name">
                        {c.srrentaladmin
                          ? c.srrentaladmin?.fullname
                          : c.srmechanicadmin?.fullname}
                      </span>
                      <span className="um-conv-time">
                        {formatTime(c.updated_at)}
                      </span>
                    </div>
                    <div className="um-conv-row2">
                      <span className="um-role">
                        {c.srrentaladmin ? "Rental Admin" : "Mechanic Admin"}
                      </span>
                    </div>
                    <div className="um-conv-row3">
                      <span className="um-conv-preview">{c.lastmessage}</span>
                      {c.unread > 0 && (
                        <span className="um-conv-unread">{c.unread}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {filtered?.length === 0 && (
                <div
                  style={{
                    padding: "20px",
                    textAlign: "center",
                    color: "#888",
                  }}
                >
                  <p>No conversations found</p>
                </div>
              )}
            </div>
          </aside>

          {/* ── CHAT PANEL ── */}
          {selectedChat ? (
            <div
              className={`um-chat ${!showSidebar ? "um-chat--visible" : ""}`}
            >
              {/* chat header */}
              <div className="um-chat-header">
                <div className="um-chat-header-left">
                  <div className="um-chat-avatar">
                   <img
                      src={`${baseUrlForFile}/${
                        selectedChat.srrentaladmin
                          ? selectedChat.srrentaladmin?.image
                          : selectedChat.srmechanicadmin?.image
                      }`}
                      alt="profile"
                      className="um-avatar-img"
                      onError={(e) => {
                        e.target.src = "/images/default-user.png";
                      }}
                    />
                  </div>
                  <div>

                    <div className="um-chat-admin-name">
                      {selectedChat.srrentaladmin
                        ? selectedChat.srrentaladmin?.fullname
                        : selectedChat.srmechanicadmin?.fullname}
                    </div>
                  </div>
                </div>
              </div>

              {/* context bar */}
              <div className="um-context-bar">
                <span>
                  Shop:{" "}
                  <strong>
                    {selectedChat.srrentaladmin
                      ? selectedChat.srrentaladmin?.srbusinessdetails?.businessname
                      : selectedChat.srmechanicadmin?.fullname}
                  </strong>
                </span>
              </div>

              {/* messages */}
              <div className="um-messages">
                {messages?.map((msg) => {
                  // sender normalize karo — 'user' right side, baaki sab left
                  const isUser = msg.sender === "user";
                  const senderClass = isUser ? "user" : "shop";

                  return (
                    <div
                      key={msg.id}
                      className={`um-msg-wrap um-msg-wrap--${senderClass}`}
                    >
                      <div className={`um-bubble um-bubble--${senderClass}`}>
                        {msg.image && msg.image.trim() !== "" && (
                          <img
                            src={`${baseUrlForFile}/${msg.image}`}
                            alt="attachment"
                            style={{
                              maxWidth: "200px",
                              borderRadius: "8px",
                              marginBottom: "4px",
                              display: "block",
                            }}
                            onError={(e) => {
                              // agar image load na ho toh path console mein dekho
                              console.log(
                                "Image failed to load:",
                                e.target.src,
                              );
                            }}
                          />
                        )}
                        {msg.message && msg.message.trim() !== "" && (
                          <span>{msg.message}</span>
                        )}
                      </div>
                      <span className="um-msg-time">
                        {formatTime(msg.created_at)}
                      </span>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* File Preview */}
              {attachedFile && (
                <div
                  style={{
                    padding: "6px 16px",
                    background: "#f0f0f0",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span>📎 {attachedFile.name}</span>
                  <button
                    onClick={() => {
                      setAttachedFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontWeight: "bold",
                    }}
                  >
                    ✕
                  </button>
                </div>
              )}

              {/* input */}
              <div className="um-input-area">
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={(e) => setAttachedFile(e.target.files[0])}
                />

                <button
                  className="um-attach-btn"
                  onClick={() => fileInputRef.current.click()}
                >
                  <FiPaperclip size={18} />
                </button>

                <input
                  className="um-input"
                  type="text"
                  placeholder="Type your message..."
                  value={newMsg}
                  onChange={(e) => setNewMsg(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                />

                <button
                  className="um-send-btn"
                  onClick={sendMessage}
                  disabled={!newMsg.trim() && !attachedFile}
                >
                  <FiSend />
                </button>
              </div>
            </div>
          ) : (
            <div
              className="um-chat"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                color: "#888",
              }}
            >
              <div style={{ fontSize: "48px" }}>💬</div>
              <h3>Select a conversation</h3>
              <p>Choose a contact to start messaging</p>
            </div>
          )}
        </div>
      </div>
    </UserMasterLayout>
  );
};

export default UserMessages;